import React, { useEffect, useState } from 'react';
import { useTheme } from '../../styles/ThemeProvider';
import { FilterBar, DateRangePicker, KpiCard } from '../../components/analytics';
import { formatTRY, formatPct } from '../../utils/analytics/format';
import { getPresetRange, toIsoDate } from '../../utils/analytics/dateRange';
import { useFirmalar } from '../../hooks/useFirmalar';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const initialRange = () => {
  const r = getPresetRange('last30Days');
  return { preset: 'last30Days', start: toIsoDate(r.start), end: toIsoDate(r.end) };
};

export default function MaliyetVaryansPage() {
  const { theme } = useTheme();
  const [range, setRange] = useState(initialRange);
  const [firma, setFirma] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { data: firmalar } = useFirmalar();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ baslangic: range.start, bitis: range.end });
        if (firma) params.append('firma', firma);
        const res = await fetch(`${API}/api/reports/maliyet-varyans?${params}`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [range.start, range.end, firma]);

  return (
    <div style={{ padding: theme.space.lg, display: 'flex', flexDirection: 'column', gap: theme.space.lg, background: theme.color.surfaceAlt, minHeight: '100vh' }}>
      <header>
        <h1 style={{ margin: 0, color: theme.color.text, fontSize: theme.font.size.xxl, fontWeight: theme.font.weight.bold }}>
          Maliyet Varyansı
        </h1>
        <div style={{ color: theme.color.textSoft, fontSize: theme.font.size.sm, marginTop: 4 }}>
          Beklenen food cost (recipe maliyeti) ile gerçekleşen food cost (stok hareketleri) arasındaki fark.
          Pozitif varyans = beklenmeyen kayıp (fire/zayi/hata).
        </div>
      </header>

      <FilterBar>
        <DateRangePicker value={range} onChange={setRange} />
        <select
          value={firma}
          onChange={(e) => setFirma(e.target.value)}
          style={{
            padding: `${theme.space.xs}px ${theme.space.md}px`,
            borderRadius: theme.radius.sm,
            border: `1px solid ${theme.color.border}`,
            background: theme.color.surface,
            color: theme.color.text,
            fontSize: theme.font.size.xs,
            fontFamily: theme.font.family,
          }}
        >
          <option value="">Tüm Firmalar</option>
          {(firmalar || []).map((f) => <option key={f.kod} value={f.kod}>{f.ad}</option>)}
        </select>
      </FilterBar>

      {loading && <div style={{ padding: theme.space.md, color: theme.color.textSoft }}>Yükleniyor...</div>}
      {error && <div style={{ padding: theme.space.md, color: theme.color.danger, background: theme.color.dangerBg, borderRadius: theme.radius.sm }}>Hata: {error}</div>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: theme.space.md }}>
            <KpiCard label="Theoretical (Beklenen)" value={formatTRY(data.theoretical_tl, { decimals: 0 })} />
            <KpiCard label="Actual (Gerçekleşen)" value={formatTRY(data.actual_tl, { decimals: 0 })} />
            <KpiCard label="Varyans (Fire/Zayi)" value={formatTRY(data.varyans_tl, { decimals: 0 })} />
            <KpiCard label="Varyans %" value={formatPct(data.varyans_pct)} />
          </div>

          <section style={{ background: theme.color.surface, padding: theme.space.lg, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}` }}>
            <h3 style={{ marginTop: 0, color: theme.color.text }}>Görsel Karşılaştırma</h3>
            {(() => {
              const max = Math.max(data.theoretical_tl || 0, data.actual_tl || 0, 1);
              const tBar = (data.theoretical_tl / max * 100).toFixed(1);
              const aBar = (data.actual_tl / max * 100).toFixed(1);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.space.md, fontFamily: 'monospace', fontSize: 14 }}>
                  <div>
                    <div style={{ marginBottom: 4 }}>Theoretical: {formatTRY(data.theoretical_tl, { decimals: 0 })}</div>
                    <div style={{ height: 28, background: theme.color.surfaceAlt, borderRadius: 4 }}>
                      <div style={{ width: `${tBar}%`, height: '100%', background: '#0d6efd', borderRadius: 4 }} />
                    </div>
                  </div>
                  <div>
                    <div style={{ marginBottom: 4 }}>Actual: {formatTRY(data.actual_tl, { decimals: 0 })} <span style={{ color: data.varyans_tl > 0 ? theme.color.danger : theme.color.success, marginLeft: 8 }}>({formatPct(data.varyans_pct, { withSign: true })})</span></div>
                    <div style={{ height: 28, background: theme.color.surfaceAlt, borderRadius: 4 }}>
                      <div style={{ width: `${aBar}%`, height: '100%', background: data.varyans_tl > 0 ? '#dc3545' : '#28a745', borderRadius: 4 }} />
                    </div>
                  </div>
                </div>
              );
            })()}
          </section>

          <section style={{ background: theme.color.surface, padding: theme.space.lg, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}` }}>
            <h3 style={{ marginTop: 0, color: theme.color.text }}>Detay</h3>
            <p style={{ color: theme.color.textSoft, fontSize: 13, lineHeight: 1.6 }}>
              <strong>Theoretical SMM:</strong> {formatTRY(data.theoretical_tl, { decimals: 2 })} — POS satış satırlarındaki recipe maliyetlerinden hesaplanır.<br />
              <strong>Actual SMM:</strong> {formatTRY(data.actual_tl, { decimals: 2 })} — Theoretical + Fire/Zayi (gerçek stok çıkışı).<br />
              <strong>Fire/Zayi:</strong> {formatTRY(data.fire_zayi_tl, { decimals: 2 })} — StokHareketi'nde referans_tipi=ZAYI olan kayıtlar.
            </p>
            {data.kategori_kirilim && data.kategori_kirilim.length === 0 && (
              <div style={{ marginTop: 12, padding: 12, background: theme.color.surfaceAlt, borderRadius: 4, fontSize: 12, color: theme.color.textSoft }}>
                Yakında: kategori bazlı kırılım + en çok sapan reçeteler.
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
